import { useParams } from 'react-router-dom';
import { Card, Spinner, Table, TableHeader, TableColumn, TableBody, TableRow, TableCell, Button } from '@/components/ui';
import { PageHeader } from '@/components/layout';
import { usePartsRequisitionDetail } from '@/api/usePartsRequisitions';
import { ROUTES } from '@/constants/routes';
import styles from './PartsRequisition.module.css';

export function PartsRequisitionView() {
    const { id } = useParams();
    const { data: requisition, isLoading } = usePartsRequisitionDetail(id || '');

    if (isLoading) {
        return <div className="flex justify-center p-8"><Spinner size="lg" /></div>;
    }

    if (!requisition) {
        return <div className={styles.container}>Pedido no encontrado</div>;
    }

    // Calcs
    const totalItems = requisition.items?.reduce((acc: number, item: any) => acc + (item.quantity || 0), 0) || 0;
    const totalCost = requisition.items?.reduce((acc: number, item: any) => acc + ((item.quantity || 0) * (item.unit_price || 0)), 0) || 0;

    return (
        <div className={styles.container}>
            <PageHeader
                title={`Pedido de Repuestos #${requisition.id}`}
                subtitle="Vista Detallada"
                backPath={ROUTES.reportsLibrary}
                actions={
                    <div className="flex gap-2">
                        <Button
                            variant="primary"
                            onClick={() => window.print()}
                        >
                            <i className="fa-solid fa-file-pdf mr-2"></i>
                            Descargar PDF
                        </Button>
                    </div>
                }
            />

            <div className={styles.topGrid}>
                {/* Left: General Info */}
                <Card className={styles.readOnlyCard} variant="full">
                    <div className={styles.cardHeaderIn}>
                        <h4 className={styles.cardTitleIn}>Información General</h4>
                    </div>
                    <div className={styles.summaryInfo}>
                        <div className={styles.infoRow}>
                            <span className={styles.infoLabel}>N° Solicitud</span>
                            <span className={styles.infoValue}>{requisition.id}</span>
                        </div>
                        <div className={styles.infoRow}>
                            <span className={styles.infoLabel}>Fecha</span>
                            <span className={styles.infoValue}>{requisition.date}</span>
                        </div>
                        <div className={styles.infoRow}>
                            <span className={styles.infoLabel}>Equipo</span>
                            <span className={styles.infoValue}>{requisition.equipment_code || requisition.equipment_id}</span>
                        </div>
                        <div className={styles.infoRow}>
                            <span className={styles.infoLabel}>Prioridad</span>
                            <span className={`${styles.infoValue} ${styles[`priority-${requisition.priority}`]}`}>
                                {requisition.priority?.toUpperCase()}
                            </span>
                        </div>

                        {requisition.justification && (
                            <div className={styles.justificationBlock}>
                                <strong>Justificación</strong>
                                <p>{requisition.justification}</p>
                            </div>
                        )}
                    </div>
                </Card>

                {/* Right: Financial Summary */}
                <Card className={styles.readOnlyCard} variant="full">
                    <div className={styles.cardHeaderIn}>
                        <h4 className={styles.cardTitleIn}>Resumen Financiero</h4>
                    </div>
                    <div className={styles.financialSummary}>
                        <div className={styles.finItem}>
                            <span>Total Items</span>
                            <strong>{totalItems}</strong>
                        </div>
                        <div className={styles.finItem}>
                            <span>Costo Total Est.</span>
                            <strong className={styles.totalCost}>${totalCost.toLocaleString()}</strong>
                        </div>
                        <div className={styles.finItem}>
                            <span>Estado Presupuesto</span>
                            <strong style={{ color: 'var(--color-success)', fontSize: 'var(--text-sm)' }}>
                                <i className="fa-solid fa-check-circle"></i> Disponible
                            </strong>
                        </div>
                    </div>
                </Card>
            </div>

            {/* Middle: Items Table */}
            <Card className={styles.readOnlyItemsCard} variant="full">
                <div className={styles.readOnlyItemsHeading}>
                    <h4 className={styles.cardTitleIn}>Lista de Artículos Solicitados</h4>
                    <span style={{ fontSize: '12px', color: 'var(--gray-500)' }}>{requisition.items?.length || 0} ITEMS TOTAL</span>
                </div>
                <Table aria-label="Lista de artículos">
                    <TableHeader>
                        <TableColumn>CÓDIGO</TableColumn>
                        <TableColumn>DESCRIPCIÓN</TableColumn>
                        <TableColumn>CANTIDAD</TableColumn>
                        <TableColumn>PRECIO UNIT.</TableColumn>
                        <TableColumn>TOTAL</TableColumn>
                    </TableHeader>
                    <TableBody>
                        {requisition.items?.map((item: any, index: number) => (
                            <TableRow key={index}>
                                <TableCell>
                                    <span style={{ fontFamily: 'Roboto Mono', fontWeight: 600 }}>
                                        {item.part_code}
                                    </span>
                                </TableCell>
                                <TableCell>
                                    <div className={styles.itemDesc}>{item.description}</div>
                                    {item.specifications && <div className={styles.itemSpec}>{item.specifications}</div>}
                                </TableCell>
                                <TableCell>
                                    <div className="flex items-center gap-2">
                                        <span className="font-bold">{item.quantity}</span>
                                        <span className="text-xs text-gray-400">UND</span>
                                    </div>
                                </TableCell>
                                <TableCell>${(item.unit_price || 0).toLocaleString()}</TableCell>
                                <TableCell><strong>${((item.quantity || 0) * (item.unit_price || 0)).toLocaleString()}</strong></TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Card>

            {/* Bottom: Logistics */}
            <Card className={styles.readOnlyCard} variant="full">
                <div className={styles.cardHeaderIn}>
                    <h4 className={styles.cardTitleIn}>Entrega y Contacto</h4>
                </div>
                <div className={styles.logisticsGrid}>
                    <div className={styles.infoRow}>
                        <span className={styles.infoLabel}>Fecha Requerida</span>
                        <span className={styles.infoValue}>{requisition.required_date || 'No especificada'}</span>
                    </div>
                    <div className={styles.infoRow}>
                        <span className={styles.infoLabel}>Lugar de Entrega</span>
                        <span className={styles.infoValue}>{requisition.delivery_location || 'Almacén Central'}</span>
                    </div>
                    <div className={styles.infoRow}>
                        <span className={styles.infoLabel}>Persona de Contacto</span>
                        <span className={styles.infoValue}>{requisition.contact_person || 'N/A'}</span>
                    </div>
                    <div className={styles.infoRow}>
                        <span className={styles.infoLabel}>Teléfono</span>
                        <span className={styles.infoValue}>{requisition.contact_phone || 'N/A'}</span>
                    </div>
                </div>
            </Card>
        </div>
    );
}
